import React, { useEffect, useState } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { GetConfigAuditLog } from "../global/Apis";

interface AuditEntry {
  id: string;
  request_id: string | null;
  event_type: string;
  environment: string;
  region: string | null;
  key: string;
  value_type: string | null;
  actor: string;
  occurred_at: string;
  status: string | null;
  error: string | null;
}

const getEventBadge = (event_type: string) => {
  switch (event_type) {
    case "config.submitted": return "bg-indigo-100 text-indigo-800";
    case "config.approved":  return "bg-blue-100 text-blue-800";
    case "config.applied":   return "bg-emerald-100 text-emerald-800";
    case "config.failed":    return "bg-rose-100 text-rose-800";
    case "config.rejected":  return "bg-amber-100 text-amber-800";
    default:                 return "bg-gray-100 text-gray-800";
  }
};

const getEventLabel = (event_type: string) => {
  switch (event_type) {
    case "config.submitted": return "Submitted";
    case "config.approved":  return "Approved";
    case "config.applied":   return "Applied";
    case "config.failed":    return "Failed";
    case "config.rejected":  return "Rejected";
    default:                 return event_type;
  }
};

const ConfigAuditLog: React.FC = () => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("all");
  const [environmentFilter, setEnvironmentFilter] = useState<string>("all");

  useEffect(() => {
    if (!account) return;
    setIsLoading(true);
    GetConfigAuditLog(instance, account)
      .then((data) => setEntries(data.entries))
      .catch((err) => setError(err.message || "Failed to load audit log"))
      .finally(() => setIsLoading(false));
  }, [instance, account]);

  const sortedEntries = [...entries].sort((a, b) => {
    const diff = new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime();
    return order === "desc" ? -diff : diff;
  });

  const filteredEntries = sortedEntries.filter((entry) => {
    if (eventTypeFilter !== "all" && entry.event_type !== eventTypeFilter) return false;
    if (environmentFilter !== "all" && entry.environment !== environmentFilter) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading audit log...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-sm">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        {error}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg border border-indigo-100 shadow-lg bg-white">
        <div className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-100">
          <h3 className="text-lg font-semibold text-gray-800">Audit log</h3>
          <p className="text-sm text-gray-500">0 entries</p>
        </div>
        <div className="text-center py-12 text-gray-400 text-sm">
          No audit log entries yet.
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-indigo-100 shadow-lg bg-white">
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-100 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Audit log</h3>
          <p className="text-sm text-gray-500">
            {filteredEntries.length === entries.length
              ? `${entries.length} ${entries.length === 1 ? "entry" : "entries"}`
              : `${filteredEntries.length} of ${entries.length} entries`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOrder(order === "desc" ? "asc" : "desc")}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors duration-150"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M5 12a1 1 0 102 0V6.414l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L5 6.414V12zM15 8a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L15 13.586V8z" />
          </svg>
          {order === "desc" ? "Newest first" : "Oldest first"}
        </button>
      </div>

      {/* Filters */}
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
        <select
          value={eventTypeFilter}
          onChange={(e) => setEventTypeFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-md px-2 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All events</option>
          <option value="config.submitted">Submitted</option>
          <option value="config.approved">Approved</option>
          <option value="config.applied">Applied</option>
          <option value="config.failed">Failed</option>
          <option value="config.rejected">Rejected</option>
        </select>

        <select
          value={environmentFilter}
          onChange={(e) => setEnvironmentFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-md px-2 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All environments</option>
          <option value="development">Development</option>
          <option value="staging">Staging</option>
          <option value="production">Production</option>
        </select>

        {(eventTypeFilter !== "all" || environmentFilter !== "all") && (
          <button
            type="button"
            onClick={() => { setEventTypeFilter("all"); setEnvironmentFilter("all"); }}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Entries */}
      <div className="divide-y divide-gray-100">
        {filteredEntries.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            No entries match the selected filters.
          </div>
        ) : (
          filteredEntries.map((entry) => (
            <div key={entry.id} className="px-5 py-3 flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <span className={`mt-0.5 px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${getEventBadge(entry.event_type)}`}>
                  {getEventLabel(entry.event_type)}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-mono text-gray-800 truncate">{entry.key}</p>
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span className={`font-medium ${
                      entry.environment === "production" ? "text-rose-600" :
                      entry.environment === "staging" ? "text-amber-600" :
                      "text-green-600"
                    }`}>
                      {entry.environment.charAt(0).toUpperCase() + entry.environment.slice(1)}
                    </span>
                    {entry.region && <span>· {entry.region.toUpperCase()}</span>}
                    {entry.value_type && (
                      <span>· {entry.value_type === "keyvault" ? "Key Vault ref" : "Plain value"}</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-gray-500">{entry.actor}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(entry.occurred_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConfigAuditLog;